import React, { useState, useEffect } from 'react'
import { Form, FormGroup, Label, Input, Modal, ModalHeader, ModalBody } from 'reactstrap'
import DataTable from 'react-data-table-component'
import { getDataWithStudent, deleteIdData, getIdData, addData as addAwardData, updateData as updateAwardData } from '../../lib/awardlist'
import { getNotesData, addData, updateData } from '../../lib/pagenotes'
import { useLayout } from '../../Hooks/useLayout'
import { useRoleBasedUrl } from '../../Hooks/useRoleBasedUrl'
import { useAuth } from '../../_helper/Auth/AuthProvider'
import parse from 'html-react-parser'
import { getStudentAwardColumns } from '../../Data/ActivitiesAwards/StudentAwardListData'
import { ClassForLessonSelect, RecognitionLevelSelect, RankingSelect } from '../Common/FormSelects'
import { ToastNotification } from '../Lessons/CourseReport/CourseReportComponents'
import { ConfirmModal } from '../Common/ConfirmModal'

const StudentAwardsList = ({ studentId, isStudentView = true, isProfile = false }) => {
	const [awardlist, setAwardList] = useState([])
	const [description, setDescription] = useState('')
	const [recId, setRecId] = useState()
	const [status, setStatus] = useState('')
	const [toastMessage, setToastMessage] = useState('')
	const [showToast, setShowToast] = useState(false)
	const [loading, setLoading] = useState(true)
	const [isDarkMode, setIsDarkMode] = useState(false)
	const [showConfirmModal, setShowConfirmModal] = useState(false)
	const [deleteId, setDeleteId] = useState(null)
	
	// Modal states
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [editMode, setEditMode] = useState('Add')
	const [editId, setEditId] = useState(null)
	const [formLoading, setFormLoading] = useState(false)
	
	// Form states
	const [gradelevel, setGradelevel] = useState()
	const [recoglevel, setRecoglevel] = useState()
	const [sortOrder, setSortOrder] = useState(0)
	const [awardDescription, setAwardDescription] = useState('')
	const [ranking, setRanking] = useState('')

	const { user } = useAuth()

	const layout = useLayout()
	const role = useRoleBasedUrl()
	const pageName = 'StudentAwardList'

	const showToastMessage = (message) => {
		setToastMessage(message)
		setShowToast(true)
		setTimeout(() => setShowToast(false), 3000)
	}

	const handleDelete = (id) => {
		setDeleteId(id)
		setShowConfirmModal(true)
	}

	const confirmDelete = async () => {
		if (deleteId) {
			try {
				await deleteIdData(deleteId)
				await getDatafromAwardList()
				showToastMessage('Award deleted successfully!')
			} catch (error) {
				console.error('Error deleting award:', error)
			} finally {
				setDeleteId(null)
			}
		}
	}

	const toggleModal = () => {
		setIsModalOpen(!isModalOpen)
		if (isModalOpen) {
			// Reset form when closing
			resetForm()
		}
	}

	const resetForm = () => {
		setEditMode('Add')
		setEditId(null)
		setGradelevel('')
		setRecoglevel('')
		setSortOrder(0)
		setAwardDescription('')
		setRanking('')
	}

	const openAddModal = () => {
		resetForm()
		setIsModalOpen(true)
	}

	const openEditModal = async (id) => {
		try {
			setFormLoading(true)
			setIsModalOpen(true)
			const awardData = await getIdData(id)
			if (awardData && awardData.length > 0) {
				setEditMode('Update')
				setEditId(id)
				setAwardDescription(awardData[0].description || '')
				setGradelevel(awardData[0].gradelevel || '')
				setRecoglevel(awardData[0].recoglevel || '')
				setSortOrder(awardData[0].sortorder || 0)
				setRanking(awardData[0].ranking || '')
			}
		} catch (error) {
			console.error('Error fetching award:', error)
		} finally {
			setFormLoading(false)
		}
	}

	const handleAwardSubmit = async (e) => {
		e.preventDefault()
		setFormLoading(true)

		try {
			const formData = {
				studentId: isStudentView ? user?.id : studentId,
				gradelevel,
				recoglevel,
				description: awardDescription,
				sortorder: sortOrder,
				ranking
			}

			if (editMode === 'Add') {
				await addAwardData(formData)
			} else {
				await updateAwardData({ id: editId, ...formData })
			}

			await getDatafromAwardList()
			
			showToastMessage(editMode === 'Add' ? 'Award added successfully!' : 'Award updated successfully!')
			toggleModal()
		} catch (error) {
			console.error('Error saving award:', error)
		} finally {
			setFormLoading(false)
		}
	}

	const getDatafromAwardList = async () => {
		try {
			setLoading(true)
			const award = await getDataWithStudent(studentId)
			setAwardList(award)
		} catch (error) {
			console.error('Error fetching awards:', error)
		} finally {
			setLoading(false)
		}
	}

	const recordCheck = async () => {
		try {
			const sdata = await getNotesData(studentId, '', pageName)
			if (sdata.length === 0) {
				setStatus('Add')
			} else {
				setStatus('Update')
				setRecId(sdata[0].id)
				getNotes()
			}
		} catch (error) {
			console.error('Error checking records:', error)
		}
	}

	const getNotes = async () => {
		try {
			const sdata = await getNotesData(studentId, '', pageName)
			if (sdata && sdata.length > 0) {
				setDescription(sdata[0].instructorNote || '')
				setRecId(sdata[0].id)
			}
		} catch (error) {
			console.error('Error fetching notes:', error)
		}
	}

	const handleSubmitforGoals = async (e) => {
		e.preventDefault()
		try {
			if (status === 'Add') {
				await addData({
					page: pageName,
					studentId: studentId,
					instructorNote: description
				})
				setStatus('Update')
			} else {
				await updateData({
					id: recId,
					page: pageName,
					studentId: studentId,
					instructorNote: description
				})
			}
			showToastMessage('Goals and Recommendation updated successfully!')
		} catch (error) {
			console.error('Error saving goals:', error)
		}
	}

	useEffect(() => {
		if (studentId) {
			recordCheck()
			getDatafromAwardList()
		}
	}, [studentId])

	useEffect(() => {
		const checkDarkMode = () => {
			setIsDarkMode(document.body.classList.contains('dark-only'))
		}
		checkDarkMode()
		const observer = new MutationObserver(checkDarkMode)
		observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
		return () => observer.disconnect()
	}, [])

	const columns = getStudentAwardColumns(layout, role, studentId, isStudentView, handleDelete, isProfile, openEditModal)

	return (
		<div className="col-12 p-0">
			<ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
			<div className="d-flex p-2 pb-0 justify-content-between align-items-center">
				<h4 className="px-2">{!isProfile && 'Student Award List'}</h4>
				{!isProfile && (
					<button
						onClick={openAddModal}
						className="btn btn-primary btn-sm"
					>
						<i className="fa fa-plus me-2"></i>
						Add New Award
					</button>
				)}
			</div>

			{loading ? (
				<div className="text-center p-4">
					<div className="spinner-border" role="status">
						<span className="visually-hidden">Loading...</span>
					</div>
					<p className="mt-2">Loading awards...</p>
				</div>
			) : (
				<>
					<div className="px-2">
						<DataTable
							columns={columns}
							data={awardlist}
							pagination
							paginationPerPage={10}
							paginationRowsPerPageOptions={[10, 25, 50, 100]}
							highlightOnHover
							striped
							responsive
							noDataComponent="No awards found"
							customStyles={{
								headCells: {
									style: {
										fontWeight: 'bold',
										fontSize: '12px',
										color: isDarkMode ? '#fff' : '#000',
										backgroundColor: isDarkMode ? '#2c323f' : '#fff'
									}
								},
								rows: {
									style: {
										color: isDarkMode ? '#fff' : '#000',
										backgroundColor: isDarkMode ? '#2c323f' : '#fff'
									}
								},
								cells: {
									style: {
										color: isDarkMode ? '#fff' : '#000'
									}
								},
								pagination: {
									style: {
										color: isDarkMode ? '#fff' : '#000',
										backgroundColor: isDarkMode ? '#2c323f' : '#fff'
									}
								}
							}}
						/>
					</div>

					<div className="px-2 mt-4">

						{/* Goals and Recommendation - Students can only view */}
						{isStudentView ? (
							<div className="p-3 rounded border">
								<h6 className="fw-bold mb-2">Goals and Recommendation:</h6>
								<div>{description ? parse(description) : 'No goals and recommendations available'}</div>
							</div>
						) : (
							<Form onSubmit={handleSubmitforGoals}>
								<FormGroup>
									<Label className="form-control-label fw-bold mb-2">Goals and Recommendation</Label>
									<Input
										type="textarea"
										rows="4"
										value={description}
										onChange={(e) => setDescription(e.target.value)}
										className="form-control border-radius-10"
										placeholder="Enter goals and recommendations"
										style={{
											resize: 'vertical',
											minHeight: '100px',
											border: '1px solid #e9ecef',
											boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
										}}
									/>
								</FormGroup>

								<div className="d-flex justify-content-end mt-3">
									<button type="submit" className="btn btn-primary">
										<i className="fa fa-save me-2"></i>
										Update Goals and Recommendation
									</button>
								</div>
							</Form>
						)}
					</div>
				</>
			)}

			{/* Award Modal */}
			<Modal isOpen={isModalOpen} toggle={toggleModal} size="lg" centered>
				<ModalHeader toggle={toggleModal}>
					{editMode === 'Add' ? 'Add Award' : 'Update Award'}
				</ModalHeader>
				<ModalBody>
					{formLoading && editMode === 'Update' ? (
						<div className="text-center p-4">
							<div className="spinner-border" role="status">
								<span className="visually-hidden">Loading...</span>
							</div>
						</div>
					) : (
						<Form onSubmit={handleAwardSubmit}>
							<FormGroup className="mb-3">
								<Label className="form-control-label fw-bold mb-2">Order</Label>
								<Input
									type="number"
									value={sortOrder}
									onChange={(e) => setSortOrder(e.target.value)}
									className="form-control border-radius-10"
									required
									style={{
										border: '1px solid #e9ecef',
										boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
										height: '45px',
										padding: '10px 15px'
									}}
								/>
							</FormGroup>

							<FormGroup className="mb-3">
								<Label className="form-control-label fw-bold mb-2">Grade Level</Label>
								<ClassForLessonSelect value={gradelevel} name="Grade Level" setValue={setGradelevel} />
							</FormGroup>

							<FormGroup className="mb-3">
								<Label className="form-control-label fw-bold mb-2">Recognition Level</Label>
								<RecognitionLevelSelect value={recoglevel} setValue={setRecoglevel} />
							</FormGroup>

							<FormGroup className="mb-3">
								<Label className="form-control-label fw-bold mb-2">Ranking</Label>
								<RankingSelect value={ranking} setValue={setRanking} />
							</FormGroup>

							<FormGroup className="mb-3">
								<Label className="form-control-label fw-bold mb-2">Award</Label>
								<Input
									type="textarea"
									rows="4"
									value={awardDescription}
									onChange={(e) => setAwardDescription(e.target.value)}
									className="form-control border-radius-10"
									placeholder="Award Description"
									style={{
										resize: 'vertical',
										minHeight: '100px',
										border: '1px solid #e9ecef',
										boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
									}}
								/>
							</FormGroup>

							<div className="d-flex justify-content-between mt-4">
								<button type="submit" className="btn btn-primary" disabled={formLoading}>
									<i className="fa fa-save me-2"></i>
									{formLoading ? 'Saving...' : editMode === 'Add' ? 'Add Award' : 'Update Award'}
								</button>
								<button type="button" onClick={toggleModal} className="btn btn-secondary">
									Cancel
								</button>
							</div>
						</Form>
					)}
				</ModalBody>
			</Modal>
			<ConfirmModal
				isOpen={showConfirmModal}
				toggle={() => setShowConfirmModal(false)}
				onConfirm={confirmDelete}
				title="Delete Award"
				message="Are you sure you want to delete this award? This action cannot be undone."
			/>
		</div>
	)
}

export { StudentAwardsList }
